import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { TransformedTrip } from "../utils/tripTransformer";
import { FlightStatusMapper } from "../utils/flightStatusMapper";
import { WarningIcon } from "../icons";
import { hasAccountFeature } from "../../site/userService";
import {
    formatConnectionTime,
    formatStatusMessage,
    isTimeDelayed,
    parseTimeString,
    getResponsiveFontSize,
    isFlightCompleted,
    getFlightCircleBorder,
    getFlightCardBorder,
    isPassengerWaitingForConnection
} from "../utils/flightPopupHelpers";

// Type definitions
interface PopupPassenger {
    name: string;
    group: string;
    tripId: string;
    pfhId: string | number;
    currentFlightHasIssue: boolean;
    futureFlightHasIssue?: boolean;
    recordLocator?: string;
}

interface PassengerDetailPopupProps {
    selectedPassenger: PopupPassenger | null;
    transformedTrip: TransformedTrip | null;
    loading: boolean;
    info: {
        type: 'flight' | 'airport';
        flightNumber: string;
        dep: string;
        arr: string;
        isCurrentFlightIssue: boolean;
        isFutureFlightIssue: boolean;
        tripCount: number;
    };
    detailPopupPosition: { left: number; top: number } | null;
    detailRef: React.RefObject<HTMLDivElement>;
    isExpanded: boolean;
}

// Connection Lines Component
const ConnectionLines: React.FC<{
    transformedTrip: TransformedTrip | null;
    flightCardsRef: React.RefObject<(HTMLDivElement | null)[]>;
    selectedPassenger: PopupPassenger | null;
    infoType: string | null;
}> = ({ transformedTrip, flightCardsRef, selectedPassenger, infoType }) => {
    const [linePositions, setLinePositions] = useState<{
        top: number;
        height: number;
        lineType: 'solid' | 'dotted' | 'split';
        left: number;
    }[]>([]);

    useEffect(() => {
        if (!transformedTrip?.flights || transformedTrip.flights.length <= 1) {
            setLinePositions([]);
            return;
        }

        const calculateLines = () => {
            const positions: {
                top: number;
                height: number;
                lineType: 'solid' | 'dotted' | 'split';
                left: number;
            }[] = [];
            const flightCards = flightCardsRef.current;
            const containerElement = flightCards && flightCards[0] ? flightCards[0].parentElement : null;

            if (!containerElement || !flightCards) return;

            for (let i = 0; i < transformedTrip.flights.length - 1; i++) {
                const currentCard = flightCards[i];
                const nextCard = flightCards[i + 1];

                if (currentCard && nextCard) {
                    const currentCircle = currentCard.querySelector('div[style*="border-radius: 50%"]') as HTMLElement;
                    const nextCircle = nextCard.querySelector('div[style*="border-radius: 50%"]') as HTMLElement;

                    if (currentCircle && nextCircle) {
                        const containerRect = containerElement.getBoundingClientRect();
                        const currentRect = currentCircle.getBoundingClientRect();
                        const nextRect = nextCircle.getBoundingClientRect();

                        const currentCenterY = currentRect.top + currentRect.height / 2 - containerRect.top;
                        const nextCenterY = nextRect.top + nextRect.height / 2 - containerRect.top;
                        const centerX = currentRect.left + currentRect.width / 2 - containerRect.left;

                        const circleRadius = currentRect.height / 2;
                        const startY = currentCenterY + circleRadius;
                        const endY = nextCenterY - circleRadius;

                        let lineType: 'solid' | 'dotted' | 'split' = 'dotted';

                        const currentFlightIndex = transformedTrip.flights.findIndex(flight => flight.isCurrentFlight);
                        const currentFlight = transformedTrip.flights[i];
                        const nextFlight = transformedTrip.flights[i + 1];

                        // Check if passenger is waiting at a connection
                        const isWaitingAtConnection =
                            infoType === 'airport' &&
                            selectedPassenger?.pfhId &&
                            currentFlight?.flightHistoryId === selectedPassenger.pfhId &&
                            nextFlight;

                        if (isWaitingAtConnection) {
                            // Show split line: top half solid (completed flight), bottom half dotted (waiting time)
                            lineType = 'split';
                        } else if (currentFlightIndex !== -1 && i < currentFlightIndex) {
                            lineType = 'solid';
                        }

                        if (Math.abs(endY - startY) > 5) {
                            positions.push({
                                top: Math.min(startY, endY),
                                height: Math.abs(endY - startY),
                                left: centerX,
                                lineType
                            });
                        }
                    }
                }
            }

            setLinePositions(positions);
        };

        const timer = setTimeout(calculateLines, 100);
        return () => clearTimeout(timer);
    }, [transformedTrip, flightCardsRef]);

    if (linePositions.length === 0) return null;

    return (
        <>
            {linePositions.map((line, index) => {
                if (line.lineType === 'split') {
                    // Render two lines: solid line at bottom (20%), dotted line at top extending to edge (80%)
                    const solidHeight = line.height * 0.30; // Solid line is 20% (at bottom near completed flight)
                    const dottedHeight = line.height * 0.70; // Dotted line is 80% (extends to edge of container)
                    return (
                        <React.Fragment key={index}>
                            {/* Dotted line at top - extends to edge of container (80%) */}
                            <div
                                className="connection-line connection-line--solid"
                                style={{
                                    left: `${line.left - 0.5}px`,
                                    top: `${line.top}px`,
                                    height: `${dottedHeight}px`
                                }}
                            />
                            {/* Solid line at bottom - near completed flight (20%) */}
                            <div
                                className="connection-line connection-line--dotted"
                                style={{
                                    left: `${line.left - 0.5}px`,
                                    top: `${line.top + dottedHeight}px`,
                                    height: `${solidHeight}px`
                                }}
                            />
                        </React.Fragment>
                    );
                }

                return (
                    <div
                        key={index}
                        className={classNames('connection-line', {
                            'connection-line--solid': line.lineType === 'solid',
                            'connection-line--dotted': line.lineType === 'dotted'
                        })}
                        style={{
                            left: `${line.left - 0.5}px`,
                            top: `${line.top}px`,
                            height: `${line.height}px`
                        }}
                    />
                );
            })}
        </>
    );
};

const PassengerDetailPopup: React.FC<PassengerDetailPopupProps> = ({
    selectedPassenger,
    transformedTrip,
    loading,
    info,
    detailPopupPosition,
    detailRef,
    isExpanded,
}) => {
    const flightCardsRef = useRef<(HTMLDivElement | null)[]>([]);

    // Helper function to check if user has Trip Assist access
    const hasTripAssistAccess = (): boolean => {
        const hasTripAssist = hasAccountFeature('TRIP_ASSIST');
        const hasTripAssistReports = hasAccountFeature('TRIP_ASSIST_REPORTS');
        return hasTripAssist || hasTripAssistReports;
    };

    // Track mouse position for tooltip positioning
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            document.documentElement.style.setProperty('--mouse-x', `${e.clientX}px`);
            document.documentElement.style.setProperty('--mouse-y', `${e.clientY}px`);
        };

        document.addEventListener('mousemove', handleMouseMove);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    if (!selectedPassenger || !detailPopupPosition) return null;

    return (
        <div
            ref={detailRef}
            className={
                info.type === 'airport'
                    ? 'locationContainer multiplePassenger passengerClick'
                    : `detailContainer ${selectedPassenger.currentFlightHasIssue ? 'withIssue' : 'withoutIssue'} multiplePassenger`
            }
            style={{
                position: "fixed",
                left: `${detailPopupPosition.left}px`,
                top: `${detailPopupPosition.top}px`,
                zIndex: 9999,
                height: "506px",
                maxHeight: "506px",
                overflowY: "auto",
                fontFamily: "Montserrat, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                width: "405px",
            }}
        >
            {/* Header */}
            <div className={
                info.type === 'airport'
                    ? 'locationHeader passengerClick'
                    : `detailHeader ${selectedPassenger.currentFlightHasIssue ? 'withIssue' : 'withoutIssue'}`
            } data-fs-exclude>
                <div className="headerText">
                    <span data-fs-exclude className="fs-exclude">
                        {selectedPassenger?.recordLocator
                            ? selectedPassenger.recordLocator.toUpperCase()
                            : (selectedPassenger && transformedTrip?.recordLocator)
                                ? transformedTrip.recordLocator.toUpperCase()
                                : (selectedPassenger && loading)
                                    ? 'Loading...'
                                    : 'Select Passenger'
                        }
                    </span>
                    {selectedPassenger?.futureFlightHasIssue && (
                        <span
                            className="warning-icon"
                            title="Future flight issue"
                            dangerouslySetInnerHTML={{ __html: WarningIcon("#FFD700") }}
                        />
                    )}
                </div>
            </div>

            {/* Detail Content */}
            <div
                className={info.type === 'airport' ? 'locationContent scrollContent' : 'detailContent scrollContent'}
                style={{
                    maxHeight: '450px',
                    height: '450px',
                    overflowY: 'auto'
                }}
            >
                {loading && (
                    <div className="content1">
                        <div className="loading-state">
                            <div
                                className={classNames('loading-spinner', {
                                    'loading-spinner--airport': info.type === 'airport',
                                    'loading-spinner--error': selectedPassenger?.currentFlightHasIssue,
                                    'loading-spinner--success': !selectedPassenger?.currentFlightHasIssue && info.type !== 'airport'
                                })}
                            />
                        </div>
                    </div>
                )}

                {!loading && !transformedTrip && (
                    <div className="content1">
                        <div className="no-data-state">
                            No trip details available
                        </div>
                    </div>
                )}

                {!loading && transformedTrip && (
                    <>
                        <div className="content1">
                            {/* Trip and Passenger Details Header */}
                            <div className="trip-detail-header fs-exclude" data-fs-exclude>
                                <div className="trip-info-column fs-exclude" data-fs-exclude>
                                    <div className="passengerLabel">Trip</div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {hasTripAssistAccess() ? (
                                            <div
                                                className="trip-id-link fs-exclude"
                                                data-fs-exclude
                                                onClick={() => {
                                                    const tripId = transformedTrip?.id || selectedPassenger?.tripId;
                                                    if (tripId) {
                                                        const tripUrl = `/trip/${tripId}`;
                                                        window.open(tripUrl, '_blank');
                                                    }
                                                }}
                                                data-tooltip={transformedTrip.recordLocator || transformedTrip.id}
                                            >
                                                {((transformedTrip.recordLocator || transformedTrip.id) || '').length > 6
                                                    ? `${(transformedTrip.recordLocator || transformedTrip.id).substring(0, 6)}...`
                                                    : (transformedTrip.recordLocator || transformedTrip.id)
                                                }
                                            </div>
                                        ) : (
                                            <div className="trip-id-text fs-exclude" data-fs-exclude>
                                                {((transformedTrip.recordLocator || transformedTrip.id) || '').length > 6
                                                    ? `${(transformedTrip.recordLocator || transformedTrip.id).substring(0, 6)}...`
                                                    : (transformedTrip.recordLocator || transformedTrip.id)
                                                }
                                            </div>
                                        )}
                                        {hasTripAssistAccess() && (
                                            <button
                                                className="trip-external-link-btn fs-exclude"
                                                data-fs-exclude
                                                onClick={() => {
                                                    const tripId = transformedTrip?.id || selectedPassenger?.tripId;
                                                    if (tripId) {
                                                        const tripUrl = `/trip/${tripId}`;
                                                        window.open(tripUrl, '_blank');
                                                    }
                                                }}
                                                data-tooltip="Open trip in new tab"
                                                style={{ background: 'none', border: 'none', padding: 0 }}
                                            >
                                                <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M11 3C10.4477 3 10 3.44772 10 4C10 4.55228 10.4477 5 11 5H13.5858L7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071C7.68342 13.0976 8.31658 13.0976 8.70711 12.7071L15 6.41421V9C15 9.55228 15.4477 10 16 10C16.5523 10 17 9.55228 17 9V4C17 3.44772 16.5523 3 16 3H11Z" fill="currentColor" />
                                                    <path d="M5 5C3.89543 5 3 5.89543 3 7V15C3 16.1046 3.89543 17 5 17H13C14.1046 17 15 16.1046 15 15V12C15 11.4477 14.5523 11 14 11C13.4477 11 13 11.4477 13 12V15H5V7H8C8.55228 7 9 6.55228 9 6C9 5.44772 8.55228 5 8 5H5Z" fill="currentColor" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="passenger-info-column">
                                    <div className="passengerBox fs-exclude" data-fs-exclude>
                                        <div className="passengerContainer">
                                            <div className="passengerLabel">Passenger(s)</div>
                                            <div
                                                className="passengerValue fs-mask"
                                                data-fs-mask
                                                style={{
                                                    cursor: (() => {
                                                        const passengerName = transformedTrip.passengerName || selectedPassenger.name || '';
                                                        return passengerName.length > 20 ? "pointer" : "default";
                                                    })()
                                                }}
                                                data-tooltip={(() => {
                                                    const passengerName = transformedTrip.passengerName || selectedPassenger.name || '';
                                                    return passengerName.length > 20 ? passengerName : undefined;
                                                })()}
                                            >
                                                {(() => {
                                                    const passengerName = transformedTrip.passengerName || selectedPassenger.name || '';
                                                    return passengerName.length > 20
                                                        ? `${passengerName.substring(0, 20)}...`
                                                        : passengerName;
                                                })()}
                                            </div>
                                        </div>
                                        <div className="passengerContainer">
                                            <div className="passengerLabel">Group</div>
                                            <div
                                                className="passengerValue fs-mask"
                                                data-fs-mask
                                                style={{
                                                    cursor: (() => {
                                                        const groupName = selectedPassenger.group || transformedTrip.groupName || '';
                                                        return groupName.length > 20 ? "pointer" : "default";
                                                    })()
                                                }}
                                                data-tooltip={(() => {
                                                    const groupName = selectedPassenger.group || transformedTrip.groupName || '';
                                                    return groupName.length > 20 ? groupName : undefined;
                                                })()}
                                            >
                                                {(() => {
                                                    const groupName = selectedPassenger.group || transformedTrip.groupName || '';
                                                    return groupName.length > 20
                                                        ? `${groupName.substring(0, 20)}...`
                                                        : groupName;
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Description */}
                            <div className="leg-description">
                                <span className="leg-description-label">Leg</span>{" "}
                                <span className="leg-description-number">
                                    {transformedTrip.currentLegIndex != null && transformedTrip.currentLegIndex >= 0
                                        ? transformedTrip.currentLegIndex + 1
                                        : "-"}
                                </span>{" "}
                                <span className="leg-description-label">of</span>{" "}
                                <span className="leg-description-number">
                                    {transformedTrip.legCount != null && transformedTrip.legCount > 0
                                        ? transformedTrip.legCount
                                        : "-"}
                                </span>{" "}
                                <span className="leg-description-label">-</span>{" "}
                                <span className="leg-description-value">
                                    {transformedTrip.currentLegDepartureCityState || "-"}
                                </span>{" "}
                                <span className="leg-description-label">to</span>{" "}
                                <span className="leg-description-value">
                                    {transformedTrip.currentLegArrivalCityState || "-"}
                                </span>
                            </div>

                            {/* Dashed Line */}
                            <div className="dashed-divider"></div>

                            {/* Current Flight Details - Location & Time */}
                            {transformedTrip.currentFlight && (
                                <>
                                    {/* Location Box */}
                                    <div className="location-row">
                                        <div className="location-column">
                                            <div className="location-info">
                                                <div className="location-code">{transformedTrip.currentFlight.departureAirportCode || "-"}</div>
                                                <div className="passengerLabel text-center">{transformedTrip.currentFlight.departureCityState || "-"}</div>
                                            </div>
                                            <div className="divider-container">
                                                <div className="divider"></div>
                                            </div>
                                        </div>
                                        <div className="arrow-column">
                                            ✈
                                        </div>
                                        <div className="location-column">
                                            <div className="divider-container">
                                                <div className="divider"></div>
                                            </div>
                                            <div className="location-info">
                                                <div className="location-code">{transformedTrip.currentFlight.arrivalAirportCode || "-"}</div>
                                                <div className="passengerLabel text-center">{transformedTrip.currentFlight.arrivalCityState || "-"}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Time Container */}
                                    <div className="time-row">
                                        <div className="time-column">
                                            <div className="time-group with-border">
                                                <div className="time-label">Scheduled</div>
                                                <div className="time-value-display">
                                                    {(() => {
                                                        const timeStr = transformedTrip.currentFlight.scheduledDepartureTime || "-";
                                                        if (timeStr === "-") {
                                                            return <span className="time">-</span>;
                                                        }
                                                        const parts = timeStr.split(' ');
                                                        const time = parts[0];
                                                        const period = parts[1];
                                                        return (
                                                            <>
                                                                <span className="time">{time}</span>
                                                                {period && (
                                                                    <span className="period">{period}</span>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="time-group">
                                                <div className="time-label">
                                                    Actual
                                                    {isTimeDelayed(
                                                        transformedTrip.currentFlight.scheduledDepartureDateTimeISO,
                                                        transformedTrip.currentFlight.actualDepartureDateTimeISO
                                                    ) && (
                                                            <div className="delay-warning">
                                                                <div className="delay-warning__triangle" />
                                                                <span className="delay-warning__exclamation">!</span>
                                                            </div>
                                                        )}
                                                </div>
                                                <div className="time-value-display">
                                                    {(() => {
                                                        const timeStr = transformedTrip.currentFlight.actualDepartureTime || "-";
                                                        const isDelayed = isTimeDelayed(
                                                            transformedTrip.currentFlight.scheduledDepartureDateTimeISO,
                                                            transformedTrip.currentFlight.actualDepartureDateTimeISO
                                                        );

                                                        if (timeStr === "-") {
                                                            return <span className={classNames('time', { 'delayed': isDelayed })}>-</span>;
                                                        }
                                                        const parsed = parseTimeString(timeStr);
                                                        if (!parsed) return <span className="time">-</span>;

                                                        return (
                                                            <>
                                                                <span className={classNames('time', { 'delayed': isDelayed })}>{parsed.time}</span>
                                                                <span className={classNames('period', { 'delayed': isDelayed })}>{parsed.period}</span>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ width: "10%" }}></div>
                                        <div className="time-column">
                                            <div className="time-group with-border">
                                                <div className="time-label">Scheduled</div>
                                                <div className="time-value-display">
                                                    {(() => {
                                                        const timeStr = transformedTrip.currentFlight.scheduledArrivalTime || "-";
                                                        if (timeStr === "-") {
                                                            return <span className="time">-</span>;
                                                        }
                                                        const parts = timeStr.split(' ');
                                                        const time = parts[0];
                                                        const period = parts[1];
                                                        return (
                                                            <>
                                                                <span className="time">{time}</span>
                                                                {period && (
                                                                    <span className="period">{period}</span>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="time-group">
                                                <div className="time-label">
                                                    Actual
                                                    {isTimeDelayed(
                                                        transformedTrip.currentFlight.scheduledArrivalDateTimeISO,
                                                        transformedTrip.currentFlight.actualArrivalDateTimeISO
                                                    ) && (
                                                            <div className="delay-warning">
                                                                <div className="delay-warning__triangle" />
                                                                <span className="delay-warning__exclamation">!</span>
                                                            </div>
                                                        )}
                                                </div>
                                                <div className="time-value-display">
                                                    {(() => {
                                                        const timeStr = transformedTrip.currentFlight.actualArrivalTime || "-";
                                                        const isDelayed = isTimeDelayed(
                                                            transformedTrip.currentFlight.scheduledArrivalDateTimeISO,
                                                            transformedTrip.currentFlight.actualArrivalDateTimeISO
                                                        );

                                                        if (timeStr === "-") {
                                                            return <span className={classNames('time', { 'delayed': isDelayed })}>-</span>;
                                                        }
                                                        const parsed = parseTimeString(timeStr);
                                                        if (!parsed) return <span className="time">-</span>;

                                                        return (
                                                            <>
                                                                <span className={classNames('time', { 'delayed': isDelayed })}>{parsed.time}</span>
                                                                <span className={classNames('period', { 'delayed': isDelayed })}>{parsed.period}</span>
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Flight Card Section */}
                        {transformedTrip.flights && transformedTrip.flights.length > 0 && (
                            <div className="flight-cards-container">
                                <ConnectionLines
                                    transformedTrip={transformedTrip}
                                    flightCardsRef={flightCardsRef}
                                    selectedPassenger={selectedPassenger}
                                    infoType={info?.type || null}
                                />
                                {transformedTrip.flights.map((flight, flightIndex) => {
                                    const statusMapping = FlightStatusMapper.mapStatus(
                                        flight.originalStatusMessage || flight.status || '',
                                        flight.statusIndicator
                                    );

                                    const statusStyle = statusMapping.color === 'Red' ? {
                                        color: "#FFFFFF",
                                        border: "1px solid #FF4C4C",
                                        backgroundColor: "#FF4C4C",
                                    } : statusMapping.color === 'Gray' ? {
                                        color: "#8998AB",
                                        border: "1px solid #8998AB",
                                        backgroundColor: "transparent",
                                    } : {
                                        color: "#55FC7F",
                                        border: "1px solid #84FFA3",
                                        backgroundColor: "transparent",
                                    };
                                    const hasConnection = flightIndex < transformedTrip.flights.length - 1 && flight.connection;

                                    return (
                                        <div
                                            key={flightIndex}
                                            ref={(el) => {
                                                if (flightCardsRef.current) {
                                                    flightCardsRef.current[flightIndex] = el;
                                                }
                                            }}
                                        >
                                            <div style={{ padding: "10px" }}>
                                                <div style={{ display: "flex" }}>
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <div style={{
                                                            background: "white",
                                                            border: (() => {
                                                                const isCompleted = isFlightCompleted(transformedTrip, flightIndex, info.type);
                                                                return getFlightCircleBorder(isCompleted, flight.isCurrentFlight);
                                                            })(),
                                                            width: "13px",
                                                            height: "13px",
                                                            borderRadius: "50%",
                                                            opacity: (() => {
                                                                const isCompleted = isFlightCompleted(transformedTrip, flightIndex, info.type);
                                                                return isCompleted ? 0.5 : 1;
                                                            })(),
                                                        }}></div>
                                                    </div>
                                                    <div style={{
                                                        border: (() => {
                                                            const isCompleted = isFlightCompleted(transformedTrip, flightIndex, info.type);
                                                            return getFlightCardBorder(isCompleted, flight.isCurrentFlight, info.type);
                                                        })(),
                                                        borderRadius: "6px",
                                                        width: "337px",
                                                        margin: "0px 8px",
                                                    }}>
                                                        <div style={{
                                                            background: "#1C2633",
                                                            borderRadius: "6px",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            minHeight: "30px",
                                                        }}>
                                                            <div style={{
                                                                width: "50%",
                                                                textAlign: "left",
                                                                color: "#FDFDFD",
                                                                padding: "0px 8px",
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                alignItems: "center",
                                                                gap: "8px",
                                                                overflow: "hidden",
                                                            }}>
                                                                <span
                                                                    style={{
                                                                        fontWeight: 700,
                                                                        fontSize: "12px",
                                                                        lineHeight: "16px",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        whiteSpace: "nowrap",
                                                                        flex: "0 1 auto",
                                                                        minWidth: 0,
                                                                        cursor: (() => flight.flightNumber && flight.flightNumber.length > 10 ? "pointer" : "default")()
                                                                    }}
                                                                    data-tooltip={(() => flight.flightNumber && flight.flightNumber.length > 10 ? flight.flightNumber : undefined)()}
                                                                >
                                                                    {flight.flightNumber || ""}
                                                                </span>
                                                                <span style={{
                                                                    fontWeight: 350,
                                                                    fontSize: "9px",
                                                                    lineHeight: "14px",
                                                                    whiteSpace: "nowrap",
                                                                    flex: "0 0 auto",
                                                                }}>{flight.departureDate || ""}</span>
                                                            </div>
                                                            <div style={{
                                                                margin: "2px 8px",
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                width: "50%",
                                                                height: "20px",
                                                                borderRadius: "12px",
                                                                fontWeight: 700,
                                                                fontSize: (() => {
                                                                    const statusText = formatStatusMessage(flight, FlightStatusMapper);
                                                                    return getResponsiveFontSize(statusText.length);
                                                                })(),
                                                                lineHeight: "12px",
                                                                textTransform: "uppercase",
                                                                ...statusStyle,
                                                            }}>
                                                                {formatStatusMessage(flight, FlightStatusMapper)}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: "flex", padding: "8px 0px" }}>
                                                            <div style={{
                                                                width: "33%",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "flex-end",
                                                                textAlign: "right",
                                                            }}>
                                                                <div style={{
                                                                    fontWeight: 500,
                                                                    fontSize: "12px",
                                                                    lineHeight: "16px",
                                                                    color: "#A9BBC7",
                                                                }}>{flight.departureAirportCode || ""}</div>
                                                                <div style={{
                                                                    fontWeight: 700,
                                                                    fontSize: "12px",
                                                                    lineHeight: "16px",
                                                                    color: "#FDFDFD",
                                                                }}>{flight.departureTime || "-"}</div>
                                                            </div>
                                                            <div style={{
                                                                width: "33%",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }}>
                                                                <div style={{
                                                                    width: "26px",
                                                                    height: "1px",
                                                                    border: "1px solid #4B5F7A",
                                                                    margin: "0px 6px",
                                                                }}></div>
                                                                <span className="arrow-column">✈</span>
                                                                <div style={{
                                                                    width: "26px",
                                                                    height: "1px",
                                                                    border: "1px solid #4B5F7A",
                                                                    margin: "0px 6px",
                                                                }}></div>
                                                            </div>
                                                            <div style={{ width: "33%" }}>
                                                                <div style={{
                                                                    fontWeight: 500,
                                                                    fontSize: "12px",
                                                                    lineHeight: "16px",
                                                                    color: "#A9BBC7",
                                                                }}>{flight.arrivalAirportCode || ""}</div>
                                                                <div style={{
                                                                    fontWeight: 700,
                                                                    fontSize: "12px",
                                                                    lineHeight: "16px",
                                                                    color: "#FDFDFD",
                                                                }}>{flight.arrivalTime || "-"}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {hasConnection && (() => {
                                                const nextFlight = transformedTrip.flights[flightIndex + 1];
                                                const isWaiting = isPassengerWaitingForConnection({
                                                    infoType: info.type,
                                                    selectedPassenger,
                                                    flight,
                                                    nextFlight
                                                });

                                                return (
                                                    <div className="connection-indicator">
                                                        <div className={classNames('connection-indicator__badge', {
                                                            'connection-indicator__badge--issue': flight.hasConnectionIssues,
                                                            'connection-indicator__badge--waiting': isWaiting,
                                                            'connection-indicator__badge--normal': !flight.hasConnectionIssues && !isWaiting
                                                        })}>
                                                            {formatConnectionTime(flight.connection) || "CONNECTION"}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PassengerDetailPopup;
